import React from "react";
import type {
  CogoElement,
  CogoBlockDef,
  CogoApp as CogoAppDef,
} from "../core/types";
import type { DesignTokens } from "../runtime/design-tokens";

interface SSRCogoRendererProps {
  manifest: CogoAppDef;
  blocks: Record<string, CogoBlockDef>;
  data?: Record<string, unknown>;
  designTokens?: DesignTokens;
  currentRoute?: string;
}

/**
 * Server-side Cogo renderer -- produces static HTML from Cogo JSON.
 * Used as the initial render in Next.js App Router for SEO and fast paint.
 * Client-side CogoApp hydrates over this with interactivity.
 *
 * This is a React Server Component: no "use client", no hooks, no event handlers.
 */
export function SSRCogoRenderer({
  manifest,
  blocks,
  data,
  designTokens,
  currentRoute,
}: SSRCogoRendererProps) {
  // Determine which page to render based on currentRoute
  const page =
    manifest.pages.find((p) => {
      if (!currentRoute) return p.route === "/";
      const pagePath = p.route.replace(/\/$/, "");
      const routePath = currentRoute.replace(/\/$/, "");
      return routePath.endsWith(pagePath) || pagePath === "/";
    }) ?? manifest.pages[0];

  if (!page) return null;

  return (
    <div data-cogo-ssr="true" data-cogo-page={page.route}>
      {page.blocks.map((blockName) => {
        const block = blocks[blockName];
        if (!block?.root) return null;
        return (
          <div key={blockName} data-cogo-block={blockName}>
            <SSRElement
              element={block.root}
              blocks={blocks}
              data={data}
              designTokens={designTokens}
            />
          </div>
        );
      })}
    </div>
  );
}

interface SSRElementProps {
  element: CogoElement;
  blocks: Record<string, CogoBlockDef>;
  data?: Record<string, unknown>;
  designTokens?: DesignTokens;
}

/**
 * Recursively renders a single CogoElement as HTML.
 * Maps Cogo element types to semantic HTML elements.
 * No hooks, no event handlers -- pure render for SSR.
 */
function SSRElement({ element, blocks, data, designTokens }: SSRElementProps) {
  if (element.visible === false) return null;

  // Resolve static text (no expression evaluation in SSR)
  const text = element.text ?? "";
  const className = element.className ?? "";

  // Cast for fields that may exist on element JSON but are not on the base CogoElement interface
  const el = element as CogoElement & Record<string, unknown>;

  switch (element.type) {
    case "container":
      return (
        <div className={className} data-testid={element.id}>
          {renderChildren(element, blocks, data, designTokens)}
        </div>
      );

    case "text":
      return (
        <span className={className} data-testid={element.id}>
          {text}
        </span>
      );

    case "button":
      return (
        <button
          className={className}
          data-testid={element.id}
          data-action-id={element.id}
        >
          {text}
          {renderChildren(element, blocks, data, designTokens)}
        </button>
      );

    case "image":
      return (
        <img
          className={className}
          data-testid={element.id}
          src={element.src ?? ""}
          alt={element.alt ?? text}
        />
      );

    case "input":
      return (
        <input
          className={className}
          data-testid={element.id}
          placeholder={element.placeholder ?? ""}
          name={element.name ?? element.id}
          readOnly
        />
      );

    case "textarea":
      return (
        <textarea
          className={className}
          data-testid={element.id}
          placeholder={element.placeholder ?? ""}
          name={element.name ?? element.id}
          readOnly
        />
      );

    case "badge":
      return (
        <span className={className} data-testid={element.id}>
          {text}
        </span>
      );

    case "icon":
      return (
        <span className={className} data-testid={element.id} aria-hidden="true">
          {renderVectorPayload(vectorPayloadOf(el))}
        </span>
      );

    case "card":
      return (
        <div className={className} data-testid={element.id}>
          {renderChildren(element, blocks, data, designTokens)}
        </div>
      );

    case "separator":
      return <hr className={className} data-testid={element.id} />;

    case "skeleton":
      return (
        <div
          className={`${className} animate-pulse`}
          data-testid={element.id}
        />
      );

    case "label":
      return (
        <label className={className} data-testid={element.id}>
          {text}
        </label>
      );

    case "checkbox":
      return (
        <div className={className} data-testid={element.id}>
          {renderVectorPayload(vectorPayloadOf(el)) ?? (
            <input type="checkbox" readOnly />
          )}
          {text && <label>{text}</label>}
        </div>
      );

    case "radio":
      return (
        <div className={className} data-testid={element.id}>
          <input type="radio" readOnly />
          {text && <label>{text}</label>}
        </div>
      );

    case "switch":
      return (
        <div className={className} data-testid={element.id}>
          <input type="checkbox" role="switch" readOnly />
          {text && <label>{text}</label>}
        </div>
      );

    case "slider":
      return (
        <input
          type="range"
          className={className}
          data-testid={element.id}
          readOnly
        />
      );

    case "select": {
      const options = (el.options ?? []) as Array<Record<string, unknown>>;
      return (
        <select className={className} data-testid={element.id}>
          {options.map((opt, i) => (
            <option key={i} value={String(opt.value ?? "")}>
              {String(opt.label ?? opt.value ?? "")}
            </option>
          ))}
        </select>
      );
    }

    case "form":
      return (
        <form className={className} data-testid={element.id}>
          {renderChildren(element, blocks, data, designTokens)}
        </form>
      );

    case "tabs":
      return (
        <div className={className} data-testid={element.id} role="tablist">
          {renderChildren(element, blocks, data, designTokens)}
        </div>
      );

    case "tab-trigger":
      return (
        <button className={className} data-testid={element.id} role="tab">
          {text}
        </button>
      );

    // Overlay elements are not rendered in SSR (client-only interactive)
    case "dialog":
    case "alert-dialog":
    case "sheet":
    case "drawer":
    case "popover":
    case "dropdown-menu":
      return null;

    case "alert":
      return (
        <div className={className} data-testid={element.id} role="alert">
          {text}
          {renderChildren(element, blocks, data, designTokens)}
        </div>
      );

    case "progress": {
      const value = el.value != null ? Number(el.value) : 0;
      return (
        <div
          className={className}
          data-testid={element.id}
          role="progressbar"
          aria-valuenow={value}
        >
          <div style={{ width: `${value}%` }} />
        </div>
      );
    }

    // Toasts are client-side only
    case "toast":
      return null;

    case "navigation-menu":
      return (
        <nav className={className} data-testid={element.id}>
          {renderChildren(element, blocks, data, designTokens)}
        </nav>
      );

    case "accordion":
      return (
        <div className={className} data-testid={element.id}>
          {renderChildren(element, blocks, data, designTokens)}
        </div>
      );

    case "table":
      return (
        <table className={className} data-testid={element.id}>
          {renderChildren(element, blocks, data, designTokens)}
        </table>
      );

    case "chart":
      return (
        <div className={className} data-testid={element.id} aria-label="Chart">
          {/* Charts require client-side JS -- render placeholder */}
          <div className="flex items-center justify-center h-48 text-muted-foreground">
            Chart
          </div>
        </div>
      );

    case "carousel":
      return (
        <div className={className} data-testid={element.id}>
          {renderChildren(element, blocks, data, designTokens)}
        </div>
      );

    case "block-ref": {
      const refBlock = element.blockType ? blocks[element.blockType] : null;
      if (!refBlock?.root) return null;
      return (
        <SSRElement
          element={refBlock.root}
          blocks={blocks}
          data={data}
          designTokens={designTokens}
        />
      );
    }

    default:
      return (
        <div className={className} data-testid={element.id}>
          {text}
          {renderChildren(element, blocks, data, designTokens)}
        </div>
      );
  }
}

function renderChildren(
  element: CogoElement,
  blocks: Record<string, CogoBlockDef>,
  data?: Record<string, unknown>,
  designTokens?: DesignTokens,
) {
  if (!element.children?.length) return null;
  return element.children.map((child) => (
    <SSRElement
      key={child.id}
      element={child}
      blocks={blocks}
      data={data}
      designTokens={designTokens}
    />
  ));
}

interface VectorPayload {
  width?: unknown;
  height?: unknown;
  paths?: unknown;
}

interface VectorPath {
  d?: unknown;
  fill?: unknown;
  fillOpacity?: unknown;
  fillRule?: unknown;
  transform?: unknown;
}

function vectorPayloadOf(el: Record<string, unknown>): VectorPayload | null {
  const direct = recordOf(el.vectorPayload);
  const nested = recordOf(recordOf(el.style).vectorPayload);
  const payload = Array.isArray(direct.paths) ? direct : nested;
  return Array.isArray(payload.paths) ? (payload as VectorPayload) : null;
}

function renderVectorPayload(payload: VectorPayload | null) {
  if (!payload) return null;
  const paths = Array.isArray(payload.paths)
    ? (payload.paths as VectorPath[])
    : [];
  if (paths.length === 0) return null;
  const width = numberOf(payload.width, 24);
  const height = numberOf(payload.height, 24);
  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      width={width}
      height={height}
      aria-hidden="true"
      focusable="false"
    >
      {paths.map((path, index) => {
        const d = stringOf(path.d);
        if (!d) return null;
        return (
          <path
            key={`${index}:${d.slice(0, 20)}`}
            d={d}
            fill={stringOf(path.fill, "currentColor")}
            fillOpacity={numberOf(path.fillOpacity, 1)}
            fillRule={fillRuleOf(path.fillRule)}
            transform={transformOf(path.transform)}
          />
        );
      })}
    </svg>
  );
}

function transformOf(value: unknown): string | undefined {
  const matrix = recordOf(value).matrix;
  if (!Array.isArray(matrix) || matrix.length !== 6) return undefined;
  const numbers = matrix.map((item) => numberOf(item, Number.NaN));
  return numbers.every(Number.isFinite)
    ? `matrix(${numbers.join(" ")})`
    : undefined;
}

function fillRuleOf(value: unknown): "evenodd" | "nonzero" | undefined {
  return value === "evenodd" || value === "nonzero" ? value : undefined;
}

function recordOf(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function stringOf(value: unknown, fallback = ""): string {
  return typeof value === "string" ? value : fallback;
}

function numberOf(value: unknown, fallback = 0): number {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
}
